import moment from 'moment';
import type { PublicScheduleEvent } from '../../../types/api/publicSchedule.types';
import type { PublicScheduleTableRow } from '../../../types/common/publicSchedule.types';
import { FALLBACK_DASH } from '../../../common/consts';
import { formatScore } from '../../../common/utils/scoreUtils';
import {
  formatDateInTimezone,
  formatTimeInTimezone,
} from '../../../common/utils/dateUtils';

/**
 * Transforms API events to DataGrid rows
 * Sorts by start_time ascending (nearest upcoming games first)
 * Handles all nullable fields gracefully
 *
 * @param events - Array of schedule events from API
 * @returns Array of table rows ready for DataGrid
 *
 * @example
 * const events = [{ id: 1, game_key: "12345", round_number: 5, start_time: "2026-04-15T19:00:00Z", ... }];
 * const rows = mapEventsToRows(events);
 * // Returns: [{ id: "12345", matchId: "12345", matchDay: "5", date: "15 Apr 2026", time: "3:00 PM EDT", ... }]
 */
export const mapEventsToRows = (
  events: Array<PublicScheduleEvent>
): Array<PublicScheduleTableRow> => {
  // Early return for edge cases
  if (!events || events.length === 0) {
    return [];
  }

  // Single-pass transformation with field mapping
  return events
    .map((event) => {
      // Format score using utility (handles null values and penalties)
      const score = formatScore(
        event.home_score,
        event.away_score,
        event.home_penalty_shootout_score,
        event.away_penalty_shootout_score
      );

      // Format date and time in game's local timezone using default formats
      const date = formatDateInTimezone(event.start_time, event.local_timezone);
      const time = formatTimeInTimezone(event.start_time, event.local_timezone);

      const homeSquad = event.home_squad_name || FALLBACK_DASH;
      const awaySquad = event.away_squad_name || FALLBACK_DASH;

      return {
        id: event.game_key ?? String(event.id), // Use game_key if available, fallback to id
        homeTeam: event.home_organisation.name,
        homeTeamLogo: event.home_organisation.logo_full_path,
        homeSquad,
        awayTeam: event.away_organisation.name,
        awayTeamLogo: event.away_organisation.logo_full_path,
        awaySquad,
        score,
        matchId: event.game_key ?? FALLBACK_DASH,
        matchDay:
          event.round_number != null
            ? String(event.round_number)
            : FALLBACK_DASH,
        competition: event.competition.name,
        date,
        time,
        location: event.event_location?.name ?? FALLBACK_DASH,
        startTimeISO: event.start_time, // Keep ISO string for sorting
        timezone: event.local_timezone,
      };
    })
    .sort((a, b) => {
      // Sort by start time ascending (nearest upcoming games first)
      return moment(a.startTimeISO).diff(moment(b.startTimeISO));
    });
};
